import { createSlice, isAnyOf } from '@reduxjs/toolkit'
import { type CognitoUser } from '@aws-amplify/auth'
import { type Role, StoreStatus, type UserInfoData } from 'common/utils/types'
import { getUserInfo } from 'common/authentication/auth.service'

import { setSpoofToken } from '../../api/apiCommon'

import {
  activateVendor,
  forgotPassword,
  getAuthUser,
  register,
  resetPassword,
  sendActivationEmail,
  sendVerificationRequest,
  signIn,
  signOut,
  startSpoofingSession,
} from './auth.service'

export interface AuthState {
  isAuthenticated: boolean
  message: string
  status: StoreStatus
  initialLoadStatus: StoreStatus
  forgotPasswordStatus: StoreStatus
  createCompanyStatus: StoreStatus
  createAccountStatus: StoreStatus
  resetPasswordStatus: StoreStatus
  sendActivationEmailStatus: StoreStatus
  sendVerificationRequestStatus: StoreStatus
  activateVendorStatus: StoreStatus
  getUserInfoStatus: StoreStatus
  userInfo: UserInfoData | null
  user: CognitoUser | null
  userId: string
  username: string
  roles: Role[] | null
  navigateTo: string
  spoofSessionToken: string | undefined
  spoofSessionStartedStatus: StoreStatus
}

const initialState: AuthState = {
  isAuthenticated: false,
  message: '',
  status: StoreStatus.Idle,
  initialLoadStatus: StoreStatus.Idle,
  resetPasswordStatus: StoreStatus.Idle,
  createCompanyStatus: StoreStatus.Idle,
  forgotPasswordStatus: StoreStatus.Idle,
  createAccountStatus: StoreStatus.Idle,
  sendActivationEmailStatus: StoreStatus.Idle,
  sendVerificationRequestStatus: StoreStatus.Idle,
  activateVendorStatus: StoreStatus.Idle,
  getUserInfoStatus: StoreStatus.Idle,
  userInfo: null,
  user: null,
  userId: '',
  username: '',
  roles: null,
  navigateTo: '',
  spoofSessionToken: localStorage.getItem('spoofSessionToken') ?? undefined,
  spoofSessionStartedStatus: StoreStatus.Idle,
}

setSpoofToken(initialState.spoofSessionToken)

export const AuthSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setSpoofingTokenToPayload: (state, { payload }) => ({
      ...state,
      spoofSessionToken: payload,
    }),
    resetStatus: (state) => ({
      ...state,
      resetPasswordStatus: StoreStatus.Idle,
      createCompanyStatus: StoreStatus.Idle,
      forgotPasswordStatus: StoreStatus.Idle,
      createAccountStatus: StoreStatus.Idle,
    }),
    resetMessage: (state) => ({
      ...state,
      message: '',
    }),
    clearAuth: () => ({
      ...initialState,
    }),
    logout: (state) => {
      // clear redux state - this is done in store.ts
    },
    setUser: (state, { payload }: { payload: CognitoUser }) => ({
      ...state,
      roles: payload.getSignInUserSession()?.getIdToken().payload['cognito:groups'],
      username: payload.getUsername() ?? '',
      userId: payload.getSignInUserSession()?.getIdToken().payload['custom:principalId'] || '',
    }),
  },
  extraReducers: (builder) => {
    builder.addCase(register.pending, (state) => ({
      ...state,
      createAccountStatus: StoreStatus.Loading,
    }))
    builder.addCase(register.fulfilled, (state) => ({
      ...state,
      createAccountStatus: StoreStatus.Succeeded,
      status: StoreStatus.Succeeded,
    }))
    builder.addCase(register.rejected, (state, { error }) => ({
      ...state,
      message: error.message ?? 'Greška prilikom registracije.',
      status: StoreStatus.Failed,
      createAccountStatus: StoreStatus.Failed,
    }))
    builder.addCase(signIn.fulfilled, (state, { payload }) => {
      return {
        ...state,
        status: StoreStatus.Succeeded,
        isAuthenticated: payload.isAuthenticated,
        user: payload.user,
      }
    })
    builder.addCase(getUserInfo.pending, (state, { payload }) => ({
      ...state,
      getUserInfoStatus: StoreStatus.Loading,
    }))
    builder.addCase(getUserInfo.fulfilled, (state, { payload }) => ({
      ...state,
      userInfo: payload,
      getUserInfoStatus: StoreStatus.Succeeded,
    }))
    builder.addCase(getUserInfo.rejected, (state) => ({
      ...state,
      userInfo: null,
      getUserInfoStatus: StoreStatus.Failed,
    }))
    builder.addCase(forgotPassword.fulfilled, (state) => ({
      ...state,
      forgotPasswordStatus: StoreStatus.Succeeded,
      status: StoreStatus.Succeeded,
    }))
    builder.addCase(resetPassword.fulfilled, (state) => ({
      ...state,
      resetPasswordStatus: StoreStatus.Succeeded,
      status: StoreStatus.Succeeded,
    }))
    builder.addCase(resetPassword.rejected, (state, { error }) => {
      let message = ''
      switch (error.code) {
        case 'InvalidParameterException':
          message = 'Polje za kod ne može da počinje sa praznim karakterom.'
          break
        case 'CodeMismatchException':
          message = 'Verifikacioni kod nije ispravan, molimo vas pokušajte opet.'
          break
        default:
          message = 'Greška pri resetovanju lozinke.'
      }
      return {
        ...state,
        message,
        status: StoreStatus.Failed,
      }
    })

    builder.addCase(signOut.fulfilled, () => ({
      ...initialState,
      isAuthenticated: false,
      initialLoadStatus: StoreStatus.Succeeded,
      navigateTo: '/auth/login',
      status: StoreStatus.Idle,
    }))
    builder.addCase(getAuthUser.pending, (state, { payload }) => ({
      ...state,
      initialLoadStatus: StoreStatus.Loading,
    }))
    builder.addCase(getAuthUser.fulfilled, (state, { payload }) => ({
      ...state,
      user: payload,
      isAuthenticated: !!payload,
      initialLoadStatus: StoreStatus.Succeeded,
    }))
    builder.addCase(getAuthUser.rejected, (state) => ({
      ...state,
      user: null,
      isAuthenticated: false,
      initialLoadStatus: StoreStatus.Failed,
      status: StoreStatus.Failed,
    }))
    builder.addCase(startSpoofingSession.pending, (state, { payload }) => ({
      ...state,
      spoofSessionStartedStatus: StoreStatus.Loading,
    }))
    builder.addCase(startSpoofingSession.fulfilled, (state, { payload }) => ({
      ...state,
      spoofSessionStarted: true,
      spoofSessionToken: payload,
      spoofSessionStartedStatus: StoreStatus.Succeeded,
    }))
    builder.addCase(startSpoofingSession.rejected, (state) => ({
      ...state,
      spoofSessionStartedStatus: StoreStatus.Failed,
    }))
    builder.addCase(sendActivationEmail.pending, (state, { payload }) => ({
      ...state,
      sendActivationEmailStatus: StoreStatus.Loading,
    }))
    builder.addCase(sendActivationEmail.fulfilled, (state, { payload }) => {
      return {
        ...state,
        sendActivationEmailStatus: StoreStatus.Succeeded,
      }
    })
    builder.addCase(sendActivationEmail.rejected, (state) => ({
      ...state,
      sendActivationEmailStatus: StoreStatus.Failed,
    }))

    builder.addCase(activateVendor.pending, (state, { payload }) => ({
      ...state,
      activateVendorStatus: StoreStatus.Loading,
    }))
    builder.addCase(activateVendor.fulfilled, (state, { payload }) => {
      return {
        ...state,
        activateVendorStatus: StoreStatus.Succeeded,
      }
    })
    builder.addCase(activateVendor.rejected, (state) => ({
      ...state,
      activateVendorStatus: StoreStatus.Failed,
    }))
    builder.addCase(sendVerificationRequest.pending, (state, { payload }) => ({
      ...state,
      sendVerificationRequestStatus: StoreStatus.Loading,
    }))
    builder.addCase(sendVerificationRequest.fulfilled, (state, { payload }) => ({
      ...state,
      sendVerificationRequestStatus: StoreStatus.Succeeded,
    }))
    builder.addCase(sendVerificationRequest.rejected, (state) => ({
      ...state,
      sendVerificationRequestStatus: StoreStatus.Failed,
    }))

    // Handle pending & rejected requests
    builder.addMatcher(
      isAnyOf(
        register.pending,
        signIn.pending,
        forgotPassword.pending,
        resetPassword.pending,
        signOut.pending,
      ),
      (state) => ({
        ...state,
        status: StoreStatus.Loading,
      }),
    )
    builder.addMatcher(
      isAnyOf(signIn.rejected, forgotPassword.rejected, signOut.rejected),
      (state, { error }) => ({
        ...state,
        status: StoreStatus.Failed,
        message: error.message ?? '',
      }),
    )
    builder.addMatcher(
      isAnyOf(signIn.fulfilled, resetPassword.fulfilled, getAuthUser.fulfilled),
      (state) => ({
        ...state,
        roles: state.user?.getSignInUserSession()?.getIdToken().payload['cognito:groups'],
        username: state.user?.getUsername() ?? '',
        userId: state.user?.getSignInUserSession()?.getIdToken().payload['custom:principalId'] || '',
      }),
    )
  },
})

export const { resetStatus, resetMessage, logout, setUser, clearAuth, setSpoofingTokenToPayload } =
  AuthSlice.actions

export default AuthSlice.reducer
